import { capitalize, isArray, isEmpty, isFunction } from "lodash";
import React, { useMemo } from "react";
import { AssetEsDto, OtherProperties } from "types/assetdetails";
import { formatArrayField } from "utils/assetUtils";
import { CellRenderers } from "utils/tableUtils";
import { CopyContent } from "views/Findings/FindingDetails/FindingDetailsSourceInfo";

export const useAssetOtherTypeFields = (assetDetails: AssetEsDto) => {
  const otherDetails = useMemo(() => {
    return { ...assetDetails?.otherProperties, name: assetDetails?.name };
  }, [assetDetails]) as OtherProperties;

  const otherFields = useMemo(() => {
    const formatOtherSubType = (otherSubType: string): string => {
      if (!otherSubType) return "";
      if (otherSubType === "CONTAINER_REGISTRY") {
        return "Artifact Registry";
      }
      return otherSubType
        .split("_")
        .map((word) => capitalize(word))
        .join(" ");
    };
    const subSectionFields =
      !isEmpty(otherDetails) && !isEmpty(otherDetails["otherSubType"])
        ? OTHER_TYPE_SUB_SECTION_MAP[
            otherDetails[
              "otherSubType"
            ] as keyof typeof OTHER_TYPE_SUB_SECTION_MAP
          ]
        : undefined;
    const otherSubType = formatOtherSubType(
      otherDetails?.["otherSubType"]?.[0] as string,
    );
    const fields = [
      // Host details such as "ipv4" and "dnsName" etc... are common across types
      // Other details will display for other sources as well where otherSubType is null/undefined
      // otherSubType only exist for source Wiz
      ...(otherDetails["otherSubType"] || assetDetails.source?.[0] !== "Wiz"
        ? [
            {
              label: "Others Type",
              name: "otherSubType",
              containerClassName: "flex-column",
              formatter: () => otherSubType,
              valueClassName: "break-content",
            },
            {
              label: "Region",
              name: "region",
              containerClassName: "flex-column",
              formatter: () => formatArrayField(assetDetails.location),
            },
            {
              label: "Architecture",
              name: "architecture",
              containerClassName: "flex-column",
              formatter: () =>
                getValuesFromOtherProperties(otherDetails, "architecture"),
            },
            {
              label: "DNS Name",
              name: "dns",
              containerClassName: "flex-column",
              formatter: () => formatArrayField(assetDetails.dnsName),
              valueClassName: "break-content",
              extra: !isEmpty(assetDetails.dnsName) ? (
                <CopyContent
                  content={assetDetails.dnsName}
                  fieldLabel="DNS Name"
                />
              ) : null,
            },
            {
              label: "Cloud Provider",
              name: "cloudProvider",
              formatter: () => formatArrayField(assetDetails.cloudProvider),
            },
            {
              label: "OS",
              name: "os",
              formatter: () => formatArrayField(assetDetails.os),
            },
            {
              label: "OS Version",
              name: "osVersion",
              containerClassName: "flex-column",
              formatter: () => formatArrayField(assetDetails.osVersion),
            },
            {
              label: "IPV4",
              name: "ipv4",
              formatter: () => formatArrayField(assetDetails.ipv4),
              extra: !isEmpty(assetDetails.ipv4) ? (
                <CopyContent content={assetDetails.ipv4} fieldLabel="IPV4" />
              ) : null,
            },
            {
              label: "ARN",
              name: "arn",
              containerClassName: "flex-column",
              formatter: () => formatArrayField(assetDetails.arn),
              extra: !isEmpty(assetDetails.arn) ? (
                <CopyContent
                  content={assetDetails.arn.join(", ")}
                  fieldLabel="ARN"
                />
              ) : null,
            },
            ...(otherSubType && subSectionFields
              ? [
                  {
                    label: `${otherSubType} Details`,
                    name: "header1",
                  },
                  ...(isFunction(subSectionFields)
                    ? subSectionFields(otherDetails)
                    : []),
                ]
              : []),
          ]
        : []),
    ];
    return fields.map((otherField) => ({
      ...otherField,
      disabledPin: true,
    }));
  }, [
    assetDetails.arn,
    assetDetails.location,
    otherDetails,
    assetDetails.cloudProvider,
    assetDetails.dnsName,
    assetDetails.ipv4,
    assetDetails.os,
    assetDetails.osVersion,
    assetDetails.source,
  ]);
  return otherFields;
};

const getContainerRegistryDetails = (otherDetails: OtherProperties) => [
  {
    label: "Native Type",
    name: "nativeTypes",
    containerClassName: "flex-column",
    formatter: () => getValuesFromOtherProperties(otherDetails, "native_types"),
  },
];

const getAccessRoleDetails = (otherDetails: OtherProperties) => [
  {
    label: "Kubernetes Cluster Name",
    name: "kubernetesClusterName",
    containerClassName: "flex-column",
    formatter: () =>
      getValuesFromOtherProperties(otherDetails, "kubernetes_cluster_name"),
  },
  {
    label: "Kubernetes Flavour",
    name: "kubernetesFlavour",
    containerClassName: "flex-column",
    formatter: () =>
      getValuesFromOtherProperties(otherDetails, "kubernetes_flavor"),
  },
  {
    label: "Native Type",
    name: "nativeTypes",
    containerClassName: "flex-column",
    formatter: () => getValuesFromOtherProperties(otherDetails, "native_types"),
  },
];

const getValuesFromOtherProperties = (
  otherDetails: OtherProperties,
  propertyKey: string,
) => {
  const prefix = otherDetails["otherSubType"]?.[0]
    ? otherDetails["otherSubType"]?.[0] + "_"
    : "";
  const key = prefix + propertyKey;
  const otherDetailsValue = otherDetails[key as keyof typeof otherDetails];
  if (!isEmpty(otherDetailsValue)) {
    return isArray(otherDetailsValue)
      ? formatArrayField(otherDetailsValue)
      : otherDetailsValue;
  }
  return CellRenderers.VALUE_OR_NA();
};

export const OTHER_TYPE_SUB_SECTION_MAP = {
  CONTAINER_REGISTRY: getContainerRegistryDetails,
  ACCESS_ROLE: getAccessRoleDetails,
};
